import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { stripTypename } from '@apollo/client/utilities';
import { useWaypointTemplateQuery } from '@dashboard/generated/wp/platform';

import { type TSubmitData } from './WaypointTemplateForm';

export type TInternalSubmitData = TSubmitData & {
  defaultPreviewVariables: string;
  testScenarios: {
    name: string;
    variables: string;
  }[];
};

export function useCurrentWaypointTemplateQuery() {
  const id = useParams<{ id: string }>().id!;
  return useWaypointTemplateQuery({ variables: { id } });
}

export default function useInitialValues(): TInternalSubmitData | null {
  const { data } = useCurrentWaypointTemplateQuery();

  // There are other queries and mutations that can update the FWaypointTemplate, so
  // we pick the properties that we will actually use in our form so that we don't
  // accidentally overwrite the form when another query updates the Template.
  const loading = data === undefined;
  const id = data?.WaypointTemplate.id;
  const defaultPreviewVariables = data?.WaypointTemplate.defaultPreviewVariables;
  const testScenarios = data?.WaypointTemplate.testScenarios;
  const activeConfiguration = data?.WaypointTemplate.activeConfiguration;

  return useMemo<TInternalSubmitData | null>(() => {
    if (loading) {
      return null;
    }

    return {
      id: id!,
      defaultPreviewVariables: JSON.stringify(defaultPreviewVariables, null, 2),
      testScenarios: (testScenarios ?? []).map(({ name, variables }) => ({
        name,
        variables: JSON.stringify(variables, null, 2),
      })),
      configuration: stripTypename(activeConfiguration!),
    };
  }, [loading, id, defaultPreviewVariables, testScenarios, activeConfiguration]);
}
